import { useCallback } from 'react'
import { useLocale } from './LocaleProvider'

export type TVars = Record<string, string | number>

/** Replace `{var}` placeholders; leave unknown placeholders intact. */
function interpolate(template: string, vars?: TVars): string {
  if (!vars) return template
  return template.replace(/\{(\w+)\}/g, (_, k: string) => (k in vars ? String(vars[k]) : `{${k}}`))
}

/**
 * Returns `t(key, vars?)` for the active locale. Resolution order:
 *  1. If `vars.count` is a number, try the plural key `${key}.${category}`
 *     (category from `Intl.PluralRules`) in the active then default catalog.
 *  2. `${key}` in the active catalog, then the default-locale catalog.
 *  3. Fall back to the key itself (so missing strings are visible, never crash).
 * Then `{var}` interpolation is applied.
 */
export function useT(): (key: string, vars?: TVars) => string {
  const { locale, catalog, defaultCatalog } = useLocale()
  return useCallback(
    (key: string, vars?: TVars): string => {
      let raw: string | undefined
      if (vars && typeof vars.count === 'number') {
        const cat = new Intl.PluralRules(locale).select(vars.count)
        raw = catalog[`${key}.${cat}`] ?? defaultCatalog[`${key}.${cat}`]
      }
      raw = raw ?? catalog[key] ?? defaultCatalog[key]
      return raw == null ? key : interpolate(raw, vars)
    },
    [locale, catalog, defaultCatalog],
  )
}
